import { useEffect, useMemo, useRef, useState } from "react";
import { ChevronDown, Search } from "lucide-react";

// Curated list of high-traffic business markets.
// Country flag rendered via emoji for crisp pixel-perfect display on dark UI.
const COUNTRIES = [
  { code: "AE", name: "United Arab Emirates", dial: "+971", flag: "🇦🇪" },
  { code: "AU", name: "Australia", dial: "+61",  flag: "🇦🇺" },
  { code: "BR", name: "Brazil", dial: "+55",     flag: "🇧🇷" },
  { code: "CA", name: "Canada", dial: "+1",      flag: "🇨🇦" },
  { code: "CH", name: "Switzerland", dial: "+41",flag: "🇨🇭" },
  { code: "CN", name: "China", dial: "+86",      flag: "🇨🇳" },
  { code: "CY", name: "Cyprus", dial: "+357",    flag: "🇨🇾" },
  { code: "DE", name: "Germany", dial: "+49",    flag: "🇩🇪" },
  { code: "ES", name: "Spain", dial: "+34",      flag: "🇪🇸" },
  { code: "FR", name: "France", dial: "+33",     flag: "🇫🇷" },
  { code: "GB", name: "United Kingdom", dial: "+44", flag: "🇬🇧" },
  { code: "HK", name: "Hong Kong", dial: "+852", flag: "🇭🇰" },
  { code: "IN", name: "India", dial: "+91",      flag: "🇮🇳" },
  { code: "IT", name: "Italy", dial: "+39",      flag: "🇮🇹" },
  { code: "JP", name: "Japan", dial: "+81",      flag: "🇯🇵" },
  { code: "MT", name: "Malta", dial: "+356",     flag: "🇲🇹" },
  { code: "MX", name: "Mexico", dial: "+52",     flag: "🇲🇽" },
  { code: "MY", name: "Malaysia", dial: "+60",   flag: "🇲🇾" },
  { code: "NG", name: "Nigeria", dial: "+234",   flag: "🇳🇬" },
  { code: "NL", name: "Netherlands", dial: "+31",flag: "🇳🇱" },
  { code: "NZ", name: "New Zealand", dial: "+64",flag: "🇳🇿" },
  { code: "PH", name: "Philippines", dial: "+63",flag: "🇵🇭" },
  { code: "PL", name: "Poland", dial: "+48",     flag: "🇵🇱" },
  { code: "QA", name: "Qatar", dial: "+974",     flag: "🇶🇦" },
  { code: "SA", name: "Saudi Arabia", dial: "+966", flag: "🇸🇦" },
  { code: "SE", name: "Sweden", dial: "+46",     flag: "🇸🇪" },
  { code: "SG", name: "Singapore", dial: "+65",  flag: "🇸🇬" },
  { code: "TH", name: "Thailand", dial: "+66",   flag: "🇹🇭" },
  { code: "TR", name: "Turkey", dial: "+90",     flag: "🇹🇷" },
  { code: "TW", name: "Taiwan", dial: "+886",    flag: "🇹🇼" },
  { code: "US", name: "United States", dial: "+1", flag: "🇺🇸" },
  { code: "VN", name: "Vietnam", dial: "+84",    flag: "🇻🇳" },
  { code: "ZA", name: "South Africa", dial: "+27", flag: "🇿🇦" },
];

const DEFAULT = COUNTRIES.find((c) => c.code === "AE");

export default function PhoneInput({ value, onChange, testid = "contact-phone" }) {
  const [country, setCountry] = useState(DEFAULT);
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [number, setNumber] = useState("");
  const wrapRef = useRef(null);
  const searchRef = useRef(null);

  // Sync upward whenever country or number change
  useEffect(() => {
    onChange?.(`${country.dial} ${number}`.trim());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [country, number]);

  // Close on outside click
  useEffect(() => {
    const onDoc = (e) => {
      if (!wrapRef.current?.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, []);

  // Close on Escape
  useEffect(() => {
    if (!open) return;
    const onKey = (e) => {
      if (e.key === "Escape") setOpen(false);
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [open]);

  // Focus search when opened
  useEffect(() => {
    if (open) setTimeout(() => searchRef.current?.focus(), 30);
    else setQuery("");
  }, [open]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return COUNTRIES;
    return COUNTRIES.filter(
      (c) =>
        c.name.toLowerCase().includes(q) ||
        c.code.toLowerCase().includes(q) ||
        c.dial.includes(q),
    );
  }, [query]);

  return (
    <div className="relative" ref={wrapRef}>
      <label
        htmlFor={`${testid}-input`}
        className="block text-[11px] font-mono uppercase tracking-[0.2em] text-neutral-300"
      >
        Phone
      </label>
      <div className="mt-2 flex items-stretch gap-0 border-b border-white/25 focus-within:border-[#D9F99D] transition-colors">
        {/* Country selector trigger */}
        <button
          type="button"
          onClick={() => setOpen((s) => !s)}
          data-testid={`${testid}-country`}
          className="flex items-center gap-2 pr-3 py-3 text-white/95 text-sm hover:text-white transition-colors focus-visible:outline-none focus-visible:text-[#D9F99D]"
          aria-haspopup="listbox"
          aria-expanded={open}
          aria-label={`Country code, currently ${country.name} ${country.dial}`}
        >
          <span className="text-base leading-none" aria-hidden="true">{country.flag}</span>
          <span className="font-mono text-sm">{country.dial}</span>
          <ChevronDown
            size={14}
            aria-hidden="true"
            className={`text-neutral-300 transition-transform ${open ? "rotate-180" : ""}`}
          />
        </button>

        <input
          id={`${testid}-input`}
          type="tel"
          inputMode="numeric"
          autoComplete="tel-national"
          value={number}
          onChange={(e) => setNumber(e.target.value.replace(/[^\d\s\-]/g, ""))}
          placeholder="50 123 4567"
          data-testid={testid}
          required
          className="flex-1 bg-transparent outline-none text-white placeholder:text-neutral-500 py-3 pl-3"
        />
      </div>

      {open && (
        <div
          className="absolute z-30 mt-2 left-0 w-full sm:w-[360px] rounded-xl border border-white/10 bg-[#0F0F0F]/95 backdrop-blur-xl shadow-[0_24px_60px_rgba(0,0,0,0.6)] overflow-hidden"
          data-testid={`${testid}-dropdown`}
          role="dialog"
          aria-label="Select country"
        >
          <div className="flex items-center gap-2 px-3 py-2.5 border-b border-white/8">
            <Search size={14} className="text-neutral-400" aria-hidden="true" />
            <input
              ref={searchRef}
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search country or code…"
              aria-label="Search countries"
              className="flex-1 bg-transparent outline-none text-white placeholder:text-neutral-500 text-sm py-1"
            />
          </div>
          <ul role="listbox" className="max-h-64 overflow-y-auto">
            {filtered.length === 0 && (
              <li className="px-4 py-3 text-sm text-neutral-400">No matches.</li>
            )}
            {filtered.map((c) => {
              const selected = c.code === country.code;
              return (
                <li key={c.code} role="option" aria-selected={selected}>
                  <button
                    type="button"
                    onClick={() => {
                      setCountry(c);
                      setOpen(false);
                    }}
                    className={`w-full flex items-center gap-3 px-3 py-2.5 text-sm text-left transition-colors focus-visible:outline-none focus-visible:bg-white/8 ${
                      selected
                        ? "bg-[#D9F99D]/10 text-white"
                        : "text-neutral-200 hover:bg-white/5 hover:text-white"
                    }`}
                  >
                    <span className="text-base leading-none" aria-hidden="true">{c.flag}</span>
                    <span className="flex-1 truncate">{c.name}</span>
                    <span className="font-mono text-xs text-neutral-300">{c.dial}</span>
                  </button>
                </li>
              );
            })}
          </ul>
        </div>
      )}
    </div>
  );
}
